import { RefObject, useEffect, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import { CalendarApi } from '@fullcalendar/core'
import moment, { Moment } from 'moment-timezone'
import { format, isSameDay, subDays } from 'date-fns'
import { Locale } from 'date-fns/locale'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { CalendarView } from '../../utils/calendarParameters.ts'
import { useTranslation } from 'react-i18next'
import { FetchPlanningDataProps } from '../../hooks/usePlanningSlots.ts'

interface UseCalendarControlsProps {
    calendarRef: RefObject<FullCalendar | null>
    currentView: string
    setCurrentView: (view: string) => void
    fetchData: (props: FetchPlanningDataProps) => void
    isGlobalPlanning: boolean
    isMobile: boolean
    initialSelectedDate?: Moment
}

interface UseCalendarControlsReturn {
    handlePrev: () => void
    handleNext: () => void
    setNewCalendarTitle: () => void
    calendarTitle: string
    secondCalendarTitle: string
    selectedDate: Moment
    setSelectedDate: (date: Moment) => void
}

export function useCalendarControls({
    calendarRef,
    currentView,
    setCurrentView,
    fetchData,
    isGlobalPlanning,
    isMobile,
    initialSelectedDate
}: UseCalendarControlsProps): UseCalendarControlsReturn {
    const { t } = useTranslation()

    const [calendarTitle, setCalendarTitle] = useState('')
    const [secondCalendarTitle, setSecondCalendarTitle] = useState('')
    const [selectedDate, setSelectedDate] = useState<Moment>(initialSelectedDate || moment())

    function handlePrev(): void {
        if (currentView === CalendarView.Current) {
            setCurrentView(CalendarView.OneMonth)
        } else {
            if (calendarRef.current) {
                calendarRef.current.getApi().prev()
                setNewCalendarTitle()
            }
        }
    }

    function handleNext(): void {
        if (currentView === CalendarView.Current) {
            setCurrentView(CalendarView.OneMonth)
        } else {
            if (calendarRef.current) {
                calendarRef.current.getApi().next()
                setNewCalendarTitle()
            }
        }
    }

    function setNewCalendarTitle(): void {
        if (calendarRef.current) {
            const start: Date = calendarRef.current.getApi().view.activeStart
            const end: Date = subDays(calendarRef.current.getApi().view.activeEnd, 1)

            fetchData({ fromDate: moment(start), toDate: moment(end), isGlobalPlanning })

            const locale: Locale = getLocalFromLanguage()

            if (isMobile) {
                const newFirstTitle: string =
                    currentView === CalendarView.OneMonth
                        ? format(start, 'MMMM yyyy', { locale })
                        : isSameDay(start, end)
                          ? format(start, 'dd-MM-yyyy', { locale })
                          : `${t('fromDate')} ${format(start, 'dd-MM-yyyy', { locale })}`

                const newSecondTitle: string =
                    currentView === CalendarView.OneMonth || isSameDay(start, end)
                        ? ''
                        : `${t('toDate').toLowerCase()} ${format(end, 'dd-MM-yyyy', { locale })}`

                if (calendarTitle !== newFirstTitle) {
                    setCalendarTitle(newFirstTitle)
                }
                if (secondCalendarTitle !== newSecondTitle) {
                    setSecondCalendarTitle(newSecondTitle)
                }
            } else {
                const newTitle: string =
                    currentView === CalendarView.OneMonth
                        ? format(start, 'MMMM yyyy', { locale })
                        : isSameDay(start, end)
                          ? format(start, 'EEEE dd MMMM yyyy', { locale })
                          : `${t('fromDate')} ${format(start, 'EEEE dd MMMM', { locale })}
                     ${t('toDate').toLowerCase()} ${format(end, 'EEEE dd MMMM yyyy', { locale })}`

                if (calendarTitle !== newTitle) {
                    queueMicrotask(() => setCalendarTitle(newTitle))
                }
            }
        }
    }

    useEffect(() => {
        if (isMobile) {
            if (currentView === CalendarView.Agenda) {
                setSelectedDate(moment())
            } else {
                if (calendarRef.current) {
                    const calendarApi: CalendarApi = calendarRef.current.getApi()
                    if (calendarApi) {
                        if (currentView === CalendarView.Current) {
                            setSelectedDate(moment())
                        } else {
                            setSelectedDate(moment(calendarApi.view.activeStart))
                        }
                        calendarApi.changeView(currentView)
                    }
                }
            }
        } else {
            if (calendarRef.current) {
                const calendarApi: CalendarApi = calendarRef.current.getApi()
                if (calendarApi) {
                    queueMicrotask(() => {
                        calendarApi.changeView(currentView)
                        setNewCalendarTitle()
                    })
                }
            }
        }
    }, [currentView])

    useEffect(() => {
        if (isMobile) {
            if (currentView !== CalendarView.Agenda && calendarRef.current) {
                const calendarApi: CalendarApi = calendarRef.current.getApi()
                if (calendarApi) {
                    calendarApi.gotoDate(selectedDate.toDate())
                    setNewCalendarTitle()
                }
            }
        } else {
            if (calendarRef.current) {
                const calendarApi: CalendarApi = calendarRef.current.getApi()
                if (calendarApi) {
                    queueMicrotask(() => {
                        calendarApi.gotoDate(selectedDate.toDate())
                        setNewCalendarTitle()
                    })
                }
            }
        }
    }, [selectedDate])

    useEffect(() => {
        if (!isMobile && currentView === CalendarView.Current) {
            setSelectedDate(moment())
        }
    }, [currentView])

    return {
        handlePrev,
        handleNext,
        setNewCalendarTitle,
        calendarTitle,
        secondCalendarTitle,
        selectedDate,
        setSelectedDate
    }
}
